import type { UserSearchHit } from '@citibox-panel/api-client';
import { firstActiveRecipient } from '../../../lib/firstActiveRecipient';
import { isAnonymized } from '../../../lib/isAnonymized';

// Who the parcel may be handed to, and when it may not, WHY — the screen owes a different sentence
// to each refusal, so the cause travels and the copy stays in the dialog.
export type RecipientEligibility =
  | { eligible: true; person: { id: number; name: string; phone: string } }
  | { eligible: false; reason: 'not-found' | 'already-titular' | 'anonymized' | 'blocked' };

// Can this resolved phone become the titular of this «Entrega»?
//
// **The hard gate is `active`, never `status`.** `active` says the account is in force — not
// blocked, not deleted — and that is the whole question: an account that cannot open the «buzón»
// would inherit a parcel nobody can collect. `status` is consulted ONLY to choose between two
// sentences, which is what keeps this decision safe from `ANONYMIZED_STATUS` turning out to be
// spelled differently than backend spells it — something that constant's own comment admits is
// unconfirmed. Get the spelling wrong and the agent reads «bloqueada» about an account that was
// deleted; the parcel is protected either way.
//
// The id crosses a TYPE BOUNDARY here, and it is load-bearing: a search hit carries it as a string
// and the «Entrega» detail as a number, so `'330148' === 330148` is false and an un-normalized
// comparison would treat the current titular as somebody else — sending a reassignment the backend
// then refuses with `delivery_recipient_is_current`. Normalized once, on the way out, so the write
// gets the number its contract asks for.
export function recipientEligibility(
  hits: UserSearchHit[],
  currentRecipientId: number | null,
): RecipientEligibility {
  // The same tie-break the header search uses, so one payload always resolves to one «Usuario».
  const resolved = firstActiveRecipient(hits);
  if (resolved === undefined) return { eligible: false, reason: 'not-found' };

  const id = Number(resolved.id);

  // Asked of the RESOLVED account and not of every hit: when a phone resolves to two accounts and
  // only one of them holds the parcel, handing it to the other one is a legitimate reassignment.
  if (currentRecipientId !== null && id === currentRecipientId) {
    return { eligible: false, reason: 'already-titular' };
  }

  if (!resolved.active) {
    return { eligible: false, reason: isAnonymized(resolved.status) ? 'anonymized' : 'blocked' };
  }

  return { eligible: true, person: { id, name: resolved.name, phone: resolved.phone } };
}
